"use client";

import { BookOpen, Scale, FolderTree, HelpCircle, ShieldCheck, GraduationCap, Sparkles, Search, Layers, FileText, GitFork, Link2, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const contentStrategyCards = [{
  icon: BookOpen,
  title: "Buying guides",
  description: "Comprehensive guides (such as choosing running shoes for road vs trail) that address key pre-purchase questions and link directly into relevant category pages and product filters."
}, {
  icon: Scale,
  title: "Product comparisons",
  description: "Placing popular models, brands or product features side by side to capture non-brand and comparison searches from shoppers evaluating their options."
}, {
  icon: FolderTree,
  title: "Category guides",
  description: "In-depth guides explaining materials, sizing, compatibility and use-cases for an entire product family, reinforcing category relevance."
}, {
  icon: HelpCircle,
  title: "FAQs and help content",
  description: "Targeted answers to specific pre-sale questions regarding sizing, specifications, delivery and care, removing hesitation before checkout."
}, {
  icon: ShieldCheck,
  title: "Product care and usage content",
  description: "Maintenance and aftercare resources (such as caring for leather footwear) that build customer trust, encourage repeat visits and attract long-tail queries."
}, {
  icon: GraduationCap,
  title: "Educational resources",
  description: "Authoritative reference material that builds topical depth across your product niche while naturally guiding buyers towards primary commercial collections."
}];
const competitorAnalysisPoints = [{
  icon: Search,
  title: "Product and category visibility",
  detail: "Identifying which page types search competitors use to rank for your priority commercial queries."
}, {
  icon: Layers,
  title: "Search intent coverage",
  detail: "Uncovering where rivals have created dedicated comparison guides, category hubs or long-tail pages that your store currently lacks."
}, {
  icon: FileText,
  title: "Content gaps",
  detail: "Pinpointing product attributes, customer questions and commercial sub-topics that competitors address but your store does not yet cover."
}, {
  icon: GitFork,
  title: "Site architecture and internal linking",
  detail: "Analysing how competing stores structure categories, subcategories, facet paths and navigation hierarchies to pass ranking signals."
}, {
  icon: Link2,
  title: "Backlink and authority signals",
  detail: "Reviewing referring domain profiles across the search landscape to identify realistic, relevant authority-building opportunities."
}, {
  icon: TrendingUp,
  title: "Commercial keyword opportunities",
  detail: "Targeting high-intent search terms based on search result page composition and identified competitor content weaknesses."
}];
export default function ContentCompetitors() {
  return <section id="ecommercecontentcompetitors" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 border-b border-border transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Content & Competitor Strategy
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Ecommerce content strategy and competitor analysis
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Random blogging does not move revenue. Every piece of content should tie directly to a product or
            collection page, capturing search demand across the buying journey and channelling qualified shoppers
            into commercial checkouts.
          </p>
        </div>

        {/* Content Strategy Cards Grid */}
        <div className="mb-20">
          <div className="max-w-3xl mb-8">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Ecommerce content strategy and buying guides
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              I design content architecture and internal linking so informational articles strengthen product and
              category pages, rather than creating disconnected blog posts that compete with commercial listings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contentStrategyCards.map((card, i) => {
            const Icon = card.icon;
            return <Card key={card.title} data-index={i} className="bg-card text-card-foreground border-border/80 shadow-xs hover:border-primary/40 transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold font-default text-foreground">
                      {card.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Competitor Analysis Section */}
        <div className="pt-12 border-t border-border/70">
          <div className="max-w-3xl mb-10">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Competitor and category visibility analysis
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Search competitors—the sites that rank for your target queries—often differ from direct retail rivals.
              I benchmark your catalogue against the stores occupying page-one search positions across six key dimensions:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competitorAnalysisPoints.map((item, i) => {
            const Icon = item.icon;
            return <Card key={item.title} data-index={i} className="bg-card text-card-foreground border-border/80 shadow-xs hover:border-primary/40 transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon className="size-4" />
                      </div>
                      <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-muted text-muted-foreground">
                        0{i + 1}
                      </span>
                    </div>
                    <CardTitle className="text-base font-semibold font-default text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.detail}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Closing Action Row */}
        <div className="mt-16 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
            <Link to="/ContactPage">
              Book a Free SEO Consultation
            </Link>
          </Button>
          <Button asChild size="default" variant="outline" className="border-border text-foreground hover:bg-muted rounded-md">
            <Link to="/ContactPage?topic=free-seo-audit">
              Get a Free SEO Audit
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}